/**
 * SettingsNav — thin sub-navigation shell mounted at /settings.
 *
 * Settings is the sole top-level Ops nav item (see Layout.jsx); Team and
 * Salary are reachable only through this internal tab strip, and the
 * legacy top-level /team and /salary paths redirect here (see App.js). No
 * SensitivePageGate is added around this shell itself — Team was not
 * gated before this move and that does not change here. Salary is gated
 * at its own route in App.js (like /analytics), since it carries private
 * salary/phone/UPI data the Settings/Team tabs do not.
 *
 * Commerce (TT-803) is the one tab that is not always shown. It needs the
 * explicit `can_commerce_control` capability — a per-person grant no role
 * implies, held by a small number of named operators — so the tab is rendered
 * only after the server confirms access, and stays hidden for everyone else
 * rather than offering a link that answers 403. The probe is cached for the
 * SPA session by `probeCommerceControlAccess`, so this costs one request per
 * load, not one per Settings navigation. The server treats the original plan's
 * `can_engine` column as an additive alias of the same grant; this tab does
 * not need to know which one a person holds, only the server's answer.
 */
import { useEffect, useState } from "react";
import SectionNav from "@/components/SectionNav";
import { prefetchTeam } from "@/features/team/teamQueryClient";
import { probeCommerceControlAccess } from "@/features/commerce/commerceControlApi";
import { Outlet } from "react-router-dom";

function prefetchTeamChunk() {
  void import("@/features/team/TeamV2Root");
}

function warmTeam() {
  prefetchTeamChunk();
  void prefetchTeam();
}

function warmSalary() {
  void import("@/features/sa/ops/SalaryOpsRoot");
}

function warmCommerce() {
  void import("@/features/commerce/CommerceControlRoot");
}

const TABS = [
  { to: "/settings", label: "Settings", end: true },
  {
    to: "/settings/team",
    label: "Team",
    end: false,
    onHover: warmTeam,
  },
  {
    to: "/settings/salary",
    label: "Salary",
    end: false,
    onHover: warmSalary,
  },
];

const COMMERCE_TAB = {
  to: "/settings/commerce",
  label: "Commerce",
  end: false,
  onHover: warmCommerce,
};

export default function SettingsNav() {
  // Hidden until proven allowed — never the other way round.
  const [commerceAllowed, setCommerceAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    probeCommerceControlAccess().then((result) => {
      if (!cancelled) setCommerceAllowed(Boolean(result?.allowed));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const tabs = commerceAllowed ? [...TABS, COMMERCE_TAB] : TABS;

  return (
    <div data-testid="settings-nav" className="min-w-0">
      <SectionNav tabs={tabs} ariaLabel="Settings sections" testIdPrefix="settings" />
      <Outlet />
    </div>
  );
}
